import { useId, useMemo, useState } from 'react';
import { navigate, lessonPath } from '../../app/router';
import { LESSONS } from '../../content/lessons';
import type { GateContext, Lesson, Question } from '../../content/types';
import { buildContext, evidenceRuns, itemStillCorrect, type Answer, type Grade } from '../../state/progress';
import { expandTrace } from '../../state/runRecord';
import { useApp } from '../../state/store';
import { parseMarkup } from '../Markup';

const NO_GRADES: Record<string, Grade> = {};

export function GatePanel({ lesson }: { lesson: Lesson }) {
  const progress = useApp((s) => s.progress);
  const runs = useApp((s) => s.runs);
  const submit = useApp((s) => s.submitGate);
  const grades = useApp((s) => s.grades[lesson.gate.id] ?? NO_GRADES);
  const blocked = useApp((s) => s.gateMessages[lesson.gate.id] ?? null);
  const record = progress.gates[lesson.gate.id];
  const passed = !!record?.passed;
  const missing = lesson.lab.steps.filter((s) => !progress.evidence[s.id]);
  const ctx = useMemo(() => buildContext(evidenceRuns(progress, runs)), [progress, runs]);
  const [answers, setAnswers] = useState<Record<string, Answer>>(() =>
    Object.fromEntries(Object.entries(record?.items ?? {}).map(([k, v]) => [k, v.answer])),
  );
  const next = LESSONS[lesson.number + 1];

  const complete = (q: Question) => itemStillCorrect(q, record?.items[q.id], progress.evidence);
  const gateSubtitle = lesson.gate.title.replace(/^Check:\s*/i, '').trim();
  const evidenceLabels = lesson.lab.steps
    .map((s) => {
      const runId = progress.evidence[s.id];
      const run = runId ? runs.find((r) => r.id === runId) : null;
      return run ? { stepId: s.id, title: s.title, number: run.number } : null;
    })
    .filter((x): x is { stepId: string; title: string; number: number } => !!x);

  return (
    <section className="gate" id={`${lesson.id}-gate`} aria-labelledby={`${lesson.id}-gate-h`}>
      <h2 id={`${lesson.id}-gate-h`}>
        <span className="phase-tag">Answer</span>
        {gateSubtitle ? <span className="phase-subtitle">{gateSubtitle}</span> : null}
      </h2>
      {passed ? (
        <div className="notice success">
          <strong>Passed.</strong> {record.summary}{' '}
          {next ? (
            <button type="button" className="btn primary small" onClick={() => navigate(lessonPath(next.id))}>
              Continue to Module {next.number}: {next.title} →
            </button>
          ) : (
            <button type="button" className="btn primary small" onClick={() => navigate('/sandbox')}>
              Open the sandbox →
            </button>
          )}
        </div>
      ) : missing.length ? (
        <div className="notice info">
          <strong>Locked until the lab is complete.</strong> These questions use numbers from your own runs. Still needed:
          <ul>
            {missing.map((s) => (
              <li key={s.id}>{parseMarkup(s.title)}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="small">
          Answer the questions below to unlock the next module.
        </p>
      )}

      {!missing.length && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const toSend = Object.fromEntries(lesson.gate.questions.filter((q) => !complete(q)).map((q) => [q.id, answers[q.id] ?? null]));
            submit(lesson.id, toSend);
          }}
        >
          <ol className="questions">
            {lesson.gate.questions.map((q) => (
              <QuestionView
                key={q.id}
                q={q}
                ctx={ctx}
                answer={answers[q.id] ?? null}
                onAnswer={(a) => setAnswers((prev) => ({ ...prev, [q.id]: a }))}
                grade={grades[q.id]}
                complete={complete(q)}
                lessonId={lesson.id}
                evidenceLabels={evidenceLabels}
              />
            ))}
          </ol>
          {blocked && (
            <div className="notice warn" role="alert">
              {parseMarkup(blocked, 'blocked')}
            </div>
          )}
          {!passed && (
            <button type="submit" className="btn primary">
              Check answers
            </button>
          )}
          {record && !passed && <span className="small muted"> Attempts: {record.attempts}</span>}
        </form>
      )}
    </section>
  );
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function QuestionView({
  q,
  ctx,
  answer,
  onAnswer,
  grade,
  complete,
  lessonId,
  evidenceLabels,
}: {
  q: Question;
  ctx: GateContext;
  answer: Answer;
  onAnswer: (a: Answer) => void;
  grade: Grade | undefined;
  complete: boolean;
  lessonId: string;
  evidenceLabels: { stepId: string; title: string; number: number }[];
}) {
  const id = useId();
  const prompt = safe(() => q.prompt(ctx), 'This question needs your lab run.');
  const table = q.table ? safe(() => q.table!(ctx), null) : null;
  const figure = q.figure ? safe(() => q.figure!(ctx), null) : null;
  const feedbackId = `${id}-fb`;
  const status = complete ? 'correct' : grade ? (grade.correct ? 'correct' : 'wrong') : 'open';
  const boundRuns = evidenceLabels.filter((e) => q.binds.includes(e.stepId));

  return (
    <li className={`question ${status}`}>
      <fieldset aria-describedby={grade || complete ? feedbackId : undefined} disabled={complete}>
        <legend>{parseMarkup(prompt, q.id)}</legend>
        {boundRuns.length > 0 && (
          <p className="small question-run">
            {boundRuns.map((e, i) => (
              <span key={e.stepId}>
                {i > 0 ? ', ' : null}
                <span className="ok-text">Run {e.number}</span>
                <span className="muted"> ({parseMarkup(e.title)})</span>
              </span>
            ))}
          </p>
        )}
        {table && (
          <div className="table-scroll">
            <table className="data small">
              <caption>{table.caption}</caption>
              <thead>
                <tr>
                  {table.headers.map((h) => (
                    <th key={h} scope="col">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((c, j) => (
                      <td key={j}>{parseMarkup(String(c), `${q.id}-${i}-${j}`)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {figure && <MiniFigure ctx={ctx} figure={figure} />}
        <AnswerInput q={q} answer={answer} onAnswer={onAnswer} id={id} />
      </fieldset>
      {(grade || complete) && (
        <div id={feedbackId} className={`feedback ${complete || grade?.correct ? 'ok' : 'bad'}`} role={grade && !grade.correct ? 'alert' : undefined}>
          <strong>{complete || grade?.correct ? 'Correct.' : 'Not yet.'}</strong> {parseMarkup(complete && !grade ? q.explanation : grade!.feedback, `${q.id}-fb`)}
          {!complete && !grade?.correct && q.review && (
            <>
              {' '}
              <a href={`${lessonPath(lessonId)}#${q.review}`} onClick={(e) => {
                e.preventDefault();
                document.getElementById(q.review!)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                document.getElementById(q.review!)?.focus();
              }}>
                Review this idea
              </a>
            </>
          )}
        </div>
      )}
    </li>
  );
}

function shuffle<T>(items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function AnswerInput({ q, answer, onAnswer, id }: { q: Question; answer: Answer; onAnswer: (a: Answer) => void; id: string }) {
  switch (q.kind) {
    case 'numeric':
      return (
        <label className="numeric-answer">
          <span className="sr-only">Your answer in {q.unit}</span>
          <input type="text" inputMode="decimal" value={answer === null || answer === undefined ? '' : String(answer)} onChange={(e) => onAnswer(e.target.value)} />{' '}
          <span>{q.unit}</span>
        </label>
      );
    case 'choice':
      return <ChoiceAnswer q={q} answer={answer} onAnswer={onAnswer} id={id} />;
    case 'match':
      return <MatchAnswer q={q} answer={answer} onAnswer={onAnswer} />;
    case 'order': {
      const order = Array.isArray(answer) && answer.length === q.items.length ? answer : q.items.map((i) => i.id);
      const move = (i: number, d: number) => {
        const next = [...order];
        const j = i + d;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        onAnswer(next);
      };
      return (
        <ol className="order">
          {order.map((itemId, i) => {
            const it = q.items.find((x) => x.id === itemId)!;
            return (
              <li key={itemId}>
                {parseMarkup(it.text, itemId)}
                <button type="button" className="icon-btn" onClick={() => move(i, -1)} aria-label={`Move up: ${it.text}`}>
                  ↑
                </button>
                <button type="button" className="icon-btn" onClick={() => move(i, 1)} aria-label={`Move down: ${it.text}`}>
                  ↓
                </button>
              </li>
            );
          })}
        </ol>
      );
    }
  }
}

function ChoiceAnswer({
  q,
  answer,
  onAnswer,
  id,
}: {
  q: Extract<Question, { kind: 'choice' }>;
  answer: Answer;
  onAnswer: (a: Answer) => void;
  id: string;
}) {
  // Shuffle once per mount so the correct option is not always in the same position.
  const options = useMemo(() => shuffle(q.options), [q.id, q.options]);
  return (
    <div className="options">
      {options.map((o) => (
        <label key={o.id} className="option">
          <input type="radio" name={id} value={o.id} checked={answer === o.id} onChange={() => onAnswer(o.id)} />
          <span>{parseMarkup(o.text, `${q.id}-${o.id}`)}</span>
        </label>
      ))}
    </div>
  );
}

function MatchAnswer({
  q,
  answer,
  onAnswer,
}: {
  q: Extract<Question, { kind: 'match' }>;
  answer: Answer;
  onAnswer: (a: Answer) => void;
}) {
  const a = (answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {}) as Record<string, string>;
  // Shuffle once per mount so dropdown order does not mirror the item order.
  const choices = useMemo(() => shuffle(q.choices), [q.id, q.choices]);
  return (
    <div className="match">
      {q.items.map((it) => (
        <label key={it.id} className="match-row">
          <span>{parseMarkup(it.text, `${q.id}-${it.id}`)}</span>
          <select value={a[it.id] ?? ''} onChange={(e) => onAnswer({ ...a, [it.id]: e.target.value })}>
            <option value="">Choose…</option>
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.text.replace(/\$([^$]+)\$/g, '$1').replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1)/($2)').replace(/\\([a-z]+)/g, '$1').replace(/[{}]/g, '')}
              </option>
            ))}
          </select>
        </label>
      ))}
      {choices.some((c) => c.text.includes('$')) && (
        <ul className="small choice-key" aria-label="Choices with formatted math">
          {choices.map((c) => (
            <li key={c.id}>{parseMarkup(c.text, `${q.id}-k-${c.id}`)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MiniFigure({ ctx, figure }: { ctx: GateContext; figure: { caption: string; series: { label: string; stepId: string }[] } }) {
  const W = 180;
  const H = 90;
  const data = figure.series.map((s) => ({ label: s.label, samples: safe(() => expandTrace(ctx.runs[s.stepId].trace), []) }));
  const all = data.flatMap((d) => d.samples.map((x) => x.z));
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const tMax = Math.max(...data.flatMap((d) => d.samples.map((x) => x.t)));
  return (
    <figure className="mini-figure">
      <figcaption className="small">{figure.caption}</figcaption>
      <div className="row gap-s wrap">
        {data.map((d) => {
          const path = d.samples
            .filter((_, i) => i % 3 === 0)
            .map((s, i) => `${i ? 'L' : 'M'}${((s.t / tMax) * (W - 10) + 5).toFixed(1)},${(H - 8 - ((s.z - lo) / (hi - lo || 1)) * (H - 16)).toFixed(1)}`)
            .join('');
          const yRef = H - 8 - ((2 - lo) / (hi - lo || 1)) * (H - 16);
          const minZ = Math.min(...d.samples.map((s) => s.z));
          return (
            <div key={d.label} className="mini">
              <svg width={W} height={H} role="img" aria-label={`${d.label}: starts at ${d.samples[0]?.z.toFixed(2)} m, lowest ${minZ.toFixed(3)} m, ends at ${d.samples[d.samples.length - 1]?.z.toFixed(3)} m.`}>
                <rect x={0} y={0} width={W} height={H} className="plot-bg" />
                <line x1={5} x2={W - 5} y1={yRef} y2={yRef} className="ref-line" />
                <path d={path} fill="none" stroke="var(--c-z)" strokeWidth={2} />
              </svg>
              <p className="small center">{d.label}</p>
            </div>
          );
        })}
      </div>
      <p className="hint">Dashed line: the 2 m hover altitude.</p>
    </figure>
  );
}
